import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/lib/data';
import { DEFAULT_VIDEOS, DEFAULT_BLOGS } from '@/lib/defaults';
import { SEO, createBlogPostSchema, createBreadcrumbSchema } from '@/components/SEO';
import { Breadcrumb } from '@/components/Breadcrumb';
import { SocialShare } from '@/components/SocialShare';
import { CardSkeleton } from '@/components/Skeletons';
import { OptimizedImage } from '@/components/OptimizedImage';
import { formatDateTR } from '@/lib/helpers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Play, Clock, Search, ArrowLeft, ArrowRight, Pin, ChevronLeft, ChevronRight } from 'lucide-react';

export default function ContentHubPage() {
  const { postId } = useParams();
  const [videos, setVideos] = useState(DEFAULT_VIDEOS);
  const [blogs, setBlogs] = useState(DEFAULT_BLOGS);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('blog');
  const [videoCat, setVideoCat] = useState('all');
  const [blogPage, setBlogPage] = useState(1);
  const [blogPages, setBlogPages] = useState(1);
  const BLOG_PER_PAGE = 9;

  useEffect(() => {
    const blogUrl = postId ? `${API}/blog?limit=100` : `${API}/blog?page=${blogPage}&limit=${BLOG_PER_PAGE}`;
    Promise.all([
      axios.get(`${API}/videos`),
      axios.get(blogUrl),
    ]).then(([vRes, bRes]) => {
      setVideos(vRes.data);
      const blogData = bRes.data;
      setBlogs(blogData.items || blogData);
      if (blogData.pages) setBlogPages(blogData.pages);
    }).catch(console.error);
  }, [blogPage, postId]);

  // Blog Post Detail View
  if (postId) {
    const post = blogs.find(b => b.id === postId);
    const relatedVideo = videos.find(v => v.service === post?.related_service);

    if (!post) {
      return (
        <div className="mx-auto max-w-4xl px-4 py-20 text-center">
          <h1 className="font-heading text-[30px] font-semibold mb-4">Yazı bulunamadı</h1>
          <Link to="/icerikler" className="text-[#1F6E7A] text-sm font-medium">← İçeriklere Dön</Link>
        </div>
      );
    }

    return (
      <div data-testid="blog-post-detail">
        <SEO title={post.title} description={post.summary?.slice(0, 160)} image={post.image} type="article" schema={[createBlogPostSchema(post), createBreadcrumbSchema([{ name: 'Ana Sayfa', url: '/' }, { name: 'İçerikler', url: '/icerikler' }, { name: post.title }])]} article={{ publishedTime: post.date, section: post.category }} />
        <Breadcrumb items={[
          { label: 'İçerikler', href: '/icerikler' },
          { label: post.title, href: `/icerikler/${post.id}`, current: true },
        ]} />
        <section className="bg-[#F5F3EF] py-12 sm:py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <Link to="/icerikler" className="text-sm text-[#5F6B76] hover:text-[#1F6E7A] mb-4 inline-flex items-center gap-1">
              <ArrowLeft size={14} /> İçerikler
            </Link>
            <div className="flex items-center gap-2 mt-3 mb-3">
              <span className="text-xs font-medium text-[#1F6E7A] bg-[#1F6E7A]/8 px-2 py-0.5 rounded-full">{post.category}</span>
              <span className="text-xs text-[#5F6B76] flex items-center gap-1"><Clock size={12} /> {post.reading_time}</span>
              {post.pinned && <span className="text-xs font-medium text-[#D8C7A6] bg-[#D8C7A6]/15 px-2 py-0.5 rounded-full flex items-center gap-1"><Pin size={10} /> Sabitlenmiş</span>}
            </div>
            <h1 className="font-heading text-[34px] sm:text-[34px] font-semibold tracking-[-0.02em] mb-4">{post.title}</h1>
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#5F6B76]">{formatDateTR(post.date)}</p>
              <SocialShare title={post.title} />
            </div>
          </div>
        </section>
        <section className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
          <OptimizedImage src={post.image} alt={post.title} className="w-full rounded-[20px] mb-8 object-cover aspect-video" sizes="(max-width: 768px) 100vw, 720px" />
          <div className="prose prose-sm max-w-none text-[#1F2937] leading-relaxed" dangerouslySetInnerHTML={{ __html: post.content }} />
          {relatedVideo && (
            <div className="mt-10 p-4 bg-[#F5F3EF] rounded-2xl">
              <h3 className="text-sm font-semibold mb-3">İlginizi Çekebilecek Video</h3>
              <div className="relative aspect-video bg-gray-100 rounded-xl overflow-hidden">
                <img src={`https://img.youtube.com/vi/${relatedVideo.id}/mqdefault.jpg`} alt={relatedVideo.title} className="w-full h-full object-cover" loading="eager" />
                <a href={`https://www.youtube.com/watch?v=${relatedVideo.id}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center"><Play size={20} className="text-[#1F6E7A] ml-0.5" fill="currentColor" /></div>
                </a>
              </div>
              <p className="text-[14px] font-medium mt-2">{relatedVideo.title}</p>
            </div>
          )}
          <div className="mt-8 p-6 bg-[#1F6E7A]/5 rounded-[20px] text-center">
            <p className="text-[14px] text-[#5F6B76] mb-3">Bu konuyla ilgili tedavimizi inceleyin</p>
            <Link to={`/hizmetler/${post.related_service}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1F6E7A] text-white text-sm font-semibold hover:bg-[#185960] transition-colors">
              Tedaviyi İncele <ArrowRight size={14} />
            </Link>
          </div>
          {/* Bottom share */}
          <div className="mt-8 pt-6 border-t border-[#E7E5E4] flex justify-center">
            <SocialShare title={post.title} />
          </div>

          {/* Related Posts */}
          {(() => {
            const sameCat = blogs.filter(b => b.id !== post.id && b.category === post.category);
            const related = sameCat.length >= 2 ? sameCat.slice(0, 2) : blogs.filter(b => b.id !== post.id).slice(0, 2);
            if (!related.length) return null;
            return (
              <div className="mt-10" data-testid="related-posts">
                <h3 className="font-heading font-semibold text-[24px] mb-5">Bunları da Okuyun</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {related.map(r => (
                    <Link key={r.id} to={`/icerikler/${r.id}`}
                      className="group bg-white rounded-[20px] border border-[#E7E5E4] overflow-hidden hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)] hover:-translate-y-0.5 transition-all"
                      data-testid="related-post-card">
                      <div className="aspect-video bg-gray-100 overflow-hidden">
                        <OptimizedImage src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" sizes="(max-width: 640px) 100vw, 340px" />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[10px] font-medium text-[#1F6E7A] bg-[#1F6E7A]/8 px-1.5 py-0.5 rounded-full">{r.category}</span>
                          <span className="text-[10px] text-[#5F6B76]">{r.reading_time}</span>
                        </div>
                        <h4 className="font-heading text-sm font-semibold line-clamp-2">{r.title}</h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })()}
        </section>
      </div>
    );
  }

  // Filter blogs
  const categories = ['all', ...new Set(blogs.filter(b => b.category).map(b => b.category))];
  const filteredBlogs = blogs.filter(b => {
    if (!b.title || !b.id) return false;
    const matchSearch = (b.title || '').toLowerCase().includes(searchTerm.toLowerCase()) || (b.summary || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = selectedCategory === 'all' || b.category === selectedCategory;
    return matchSearch && matchCat;
  });

  const videoCategories = ['all', ...new Set(videos.filter(v => v.category).map(v => v.category))];
  const filteredVideos = videos.filter(v => v.id && (videoCat === 'all' || v.category === videoCat));

  return (
    <div data-testid="content-hub-page">
      <SEO title="İçerikler" description="Fonksiyonel tıp hakkında bilgilendirici blog yazıları ve video içerikler." />
      <section className="bg-[#F5F3EF] py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
          <h1 className="font-heading text-[34px] sm:text-[34px] lg:text-[46px] font-semibold tracking-[-0.02em] mb-4">İçerikler</h1>
          <p className="text-[22px] text-[#5F6B76] max-w-xl">Fonksiyonel tıp hakkında bilgilendirici yazılar ve videolar.</p>
        </div>
      </section>
      <Breadcrumb items={[{ label: 'İçerikler', href: '/icerikler', current: true }]} />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-12">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 bg-[#F5F3EF] rounded-full p-1">
            <TabsTrigger value="blog" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="video" className="rounded-full px-6 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-video">Videolar</TabsTrigger>
          </TabsList>

          <TabsContent value="blog">
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F6B76]" />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Yazı ara..."
                  className="w-full pl-9 pr-4 py-2.5 rounded-full border border-[#E7E5E4] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1F6E7A] transition-shadow"
                  data-testid="blog-search-input" />
              </div>
              <div className="flex gap-2 overflow-x-auto hide-scrollbar">
                {categories.map(cat => (
                  <button key={cat} onClick={() => setSelectedCategory(cat)}
                    className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-colors ${selectedCategory === cat ? 'bg-[#1F6E7A] text-white' : 'bg-[#F5F3EF] text-[#5F6B76] hover:bg-[#E7E5E4]'}`}
                    data-testid="blog-category-filter">
                    {cat === 'all' ? 'Tümü' : cat}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {filteredBlogs.map(post => (
                    <Link key={post.id} to={`/icerikler/${post.id}`}
                      className="group bg-white rounded-[28px] border border-[#E7E5E4] overflow-hidden hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)] hover:-translate-y-0.5 transition-all"
                      data-testid="blog-card">
                      <div className="relative aspect-video bg-gray-100 overflow-hidden">
                        <OptimizedImage src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" sizes="(max-width: 768px) 100vw, 33vw" />
                        {post.pinned && (
                          <span className="absolute top-3 left-3 text-[10px] font-medium bg-[#D8C7A6] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Pin size={10} /> Sabitlenmiş
                          </span>
                        )}
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium text-[#1F6E7A] bg-[#1F6E7A]/8 px-2 py-0.5 rounded-full">{post.category}</span>
                          <span className="text-xs text-[#5F6B76] flex items-center gap-1"><Clock size={12} /> {post.reading_time}</span>
                        </div>
                        <h3 className="font-heading font-semibold text-[22px] mb-2 line-clamp-2">{post.title}</h3>
                        <p className="text-[14px] text-[#5F6B76] line-clamp-2">{post.summary}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                {filteredBlogs.length === 0 && (
                  <div className="text-center py-12"><p className="text-sm text-[#5F6B76]">Aramanızla eşleşen içerik bulunamadı.</p></div>
                )}
            {/* Pagination */}
            {blogPages > 1 && !searchTerm && selectedCategory === 'all' && (
              <div className="flex items-center justify-center gap-2 mt-10" data-testid="blog-pagination">
                <button onClick={() => setBlogPage(p => Math.max(1, p - 1))} disabled={blogPage <= 1}
                  className="w-9 h-9 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#5F6B76] hover:bg-[#F5F3EF] disabled:opacity-30 transition-colors"
                  data-testid="pagination-prev">
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: blogPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setBlogPage(p)}
                    className={`w-9 h-9 rounded-full text-xs font-medium transition-colors ${p === blogPage ? 'bg-[#1F6E7A] text-white' : 'border border-[#E7E5E4] text-[#5F6B76] hover:bg-[#F5F3EF]'}`}
                    data-testid={`pagination-page-${p}`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => setBlogPage(p => Math.min(blogPages, p + 1))} disabled={blogPage >= blogPages}
                  className="w-9 h-9 rounded-full border border-[#E7E5E4] flex items-center justify-center text-[#5F6B76] hover:bg-[#F5F3EF] disabled:opacity-30 transition-colors"
                  data-testid="pagination-next">
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="video">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-8">
              {videoCategories.map(cat => (
                <button key={cat} onClick={() => setVideoCat(cat)}
                  className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-colors ${videoCat === cat ? 'bg-[#1F6E7A] text-white' : 'bg-[#F5F3EF] text-[#5F6B76] hover:bg-[#E7E5E4]'}`}>
                  {cat === 'all' ? 'Tümü' : cat}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVideos.map(v => (
                  <div key={v.id} className="bg-white rounded-[20px] border border-[#E7E5E4] overflow-hidden hover:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.18)] transition-shadow" data-testid="video-card">
                    <div className="relative aspect-video bg-gray-100">
                      <img src={`https://img.youtube.com/vi/${v.id}/mqdefault.jpg`} alt={v.title} className="w-full h-full object-cover" loading="eager" />
                      <a href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg"><Play size={20} className="text-[#1F6E7A] ml-0.5" fill="currentColor" /></div>
                      </a>
                    </div>
                    <div className="p-4">
                      <h3 className="text-[14px] font-medium text-[#1F2937] mb-1">{v.title}</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#5F6B76]">{v.category}</span>
                        <Link to={`/hizmetler/${v.service}`} className="text-xs font-medium text-[#1F6E7A] hover:underline">İlgili hizmet &rarr;</Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
